/**
 * @fileoverview Full-page shell for auth screens.
 * @description Centers its children (a login/register/etc. card) on the gradient background with
 * a soft radial glow, and renders the shared footer below. Reusable for any future auth page, not
 * just login. The footer's Privacy Policy/Terms & Conditions/Contact Support links navigate to
 * their real in-app pages (see routes.constants.ts).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { appConfig } from '@/config/app-config'
import LinkButton from '@/shared/components/atoms/LinkButton'
import LanguageSwitcher from '@/shared/components/molecules/LanguageSwitcher'
import { ROUTES } from '@/shared/constants/routes.constants'
import { SHOW_LANGUAGE_SWITCHER } from '@/shared/lib/i18n/language.constants'

import type { ReactNode } from 'react'

interface AuthLayoutProps {
  children: ReactNode
}

/**
 * @component
 * @param {AuthLayoutProps} props - The card content to center on the page.
 * @param {ReactNode} props.children - The card content to center on the page.
 */
function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[linear-gradient(145deg,#EEF4FD_0%,#F3F7FC_100%)] px-4 py-10 sm:px-6">
      {SHOW_LANGUAGE_SWITCHER && (
        <div className="absolute right-4 top-4 z-10">
          <LanguageSwitcher />
        </div>
      )}
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(37,99,235,0.07)_0%,transparent_70%)]"
        aria-hidden="true"
      />
      <div className="relative z-10 flex w-full max-w-[444px] flex-col items-center">
        {children}
        <p className="mt-5 text-center leading-[1.9] text-footer">
          <span className="text-xs">
            {appConfig.version} · © {appConfig.copyrightYear} {appConfig.name} ·{' '}
          </span>
          <LinkButton
            size="xs"
            tone="footer"
            data-testid="auth-footer-privacy-policy-link"
            onClick={() => void navigate(ROUTES.PRIVACY_POLICY)}
          >
            {t('legalNav.privacyPolicy')}
          </LinkButton>
          <span className="text-xs"> · </span>
          <LinkButton
            size="xs"
            tone="footer"
            data-testid="auth-footer-terms-link"
            onClick={() => void navigate(ROUTES.TERMS_AND_CONDITIONS)}
          >
            {t('legalNav.termsAndConditions')}
          </LinkButton>
          <span className="text-xs"> · </span>
          <LinkButton
            size="xs"
            tone="footer"
            data-testid="auth-footer-contact-support-link"
            onClick={() => void navigate(ROUTES.CONTACT_SUPPORT)}
          >
            {t('legalNav.contactSupport')}
          </LinkButton>
        </p>
      </div>
    </div>
  )
}

export default AuthLayout
